import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

// The WebView keeps the SPA in memory across backgrounding, so a device can
// resume on top of a build that no longer exists on the server. On resume,
// compare the current served index.html ETag with the one served when this
// session started; if they differ, a new build is live and we reload before
// the user can hit a stale lazy chunk.
//
// Why this manifests on Android and not iOS: iOS (WKWebView) aggressively
// kills backgrounded apps, so users almost always cold-start and re-fetch
// index.html — the stale window is tiny. Android (Chromium WebView) keeps
// processes alive for hours/days and resumes the in-memory SPA as-is, so old
// chunk hashes linger.
let initialEtag: string | null = null;

const fetchIndexEtag = async (): Promise<string | null> => {
  try {
    const res = await fetch(new URL('/', window.location.href), {
      method: 'HEAD',
      cache: 'no-store',
    });
    return res.headers.get('etag');
  } catch {
    return null;
  }
};

const useFreshAppShell = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    fetchIndexEtag().then((etag) => {
      if (!cancelled) initialEtag = etag;
    });

    const subscription = App.addListener('appStateChange', async ({ isActive }) => {
      if (!isActive || !initialEtag) return;
      const current = await fetchIndexEtag();
      if (current && current !== initialEtag) {
        window.location.reload();
      }
    });

    return () => {
      cancelled = true;
      subscription.then((s) => s.remove());
    };
  }, []);
};

export const FreshAppShell = () => {
  useFreshAppShell();
  return null;
};
